import { lightTheme } from '@/customTheme'
import { useStyletron } from 'baseui'
import { useState } from 'react'
import GradientSpinner from '@/components/GradientSpinner'
import TryAgainBtn from '@/scenes/Editor/components/FloatingImprover/TryAgainBtn'
import Icons from '@/scenes/Editor/components/Icons'

interface WrapAnimationImageProps {
  src_bg: string | URL
  src_fg: string | URL
  handleSelectStyle: () => void
  hasPremium: boolean
  imageLoading: boolean
  styleIdSelected: number | null
  styleId: number
  /**
   * Shows the "try again" overlay (dark scrim + refresh button) on the
   * selected tile. Editor default; Batch Studio's read-only style browser
   * turns it off since re-clicking a selected tile there does nothing.
   */
  showTryAgainOnActive?: boolean
}

function WrapAnimationImage({
  src_bg,
  src_fg,
  handleSelectStyle,
  hasPremium,
  imageLoading,
  styleIdSelected,
  styleId,
  showTryAgainOnActive = true,
}: WrapAnimationImageProps) {
  const [isHover, setIsHover] = useState(false)
  const [imagesLoaded, setImagesLoaded] = useState({ bg: false, fg: false })
  const [css] = useStyletron()
  
  const allImagesLoaded = imagesLoaded.bg && imagesLoaded.fg
  const isWipeActive = isHover && styleId !== styleIdSelected

  return (
    <div
      onMouseEnter={() => allImagesLoaded && setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        pointerEvents: imageLoading ? 'none' : 'auto',
        cursor: imageLoading ? 'not-allowed' : 'pointer',
        borderRadius: '8px',
        overflow: 'hidden',
        background: lightTheme.colors.grayScale50
      }}
      onClick={handleSelectStyle}
    >
      <img 
        src={typeof src_bg === 'string' ? src_bg : src_bg.toString()} 
        alt={'old'} 
        draggable={false}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: '8px',
        }}
        loading="lazy" 
        onLoad={() => setImagesLoaded(prev => ({ ...prev, bg: true }))}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '8px',
          overflow: 'hidden',
          animation: isWipeActive ? 'clipCrop 4s infinite' : 'none',
          zIndex: isWipeActive ? 1 : -1,
        }}
      >
        <img
          src={typeof src_fg === 'string' ? src_fg : src_fg.toString()}
          alt={'new'}
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '8px',
            position: 'absolute',
            inset: 0,
          }}
          loading="lazy"
          onLoad={() => setImagesLoaded(prev => ({ ...prev, fg: true }))}
        />
      </div>
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: '2px',
          background: 'white',
          animation: isWipeActive ? 'swipeLine 4s infinite' : 'none',
          zIndex: 1,
          boxShadow: isWipeActive ? '0px 0px 10px 0px rgba(0, 0, 0, 0.80)' : 'none',
          left: '100%',
        }}
      ></div>
      {imageLoading ? (
        styleIdSelected === styleId ? (
          <div
            className="overlay"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              background: 'rgba(0,0,0,0.65)',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '8px',
            }}
          >
            <GradientSpinner />
          </div>
        ) : (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.5)',
              pointerEvents: 'none',
              zIndex: 2,
              borderRadius: '8px',
            }}
          ></div>
        )
      ) : null}
      {!imageLoading && styleIdSelected === styleId ? (
        <>
          {showTryAgainOnActive ? (
            <div
              className={css({
                boxSizing: 'border-box',
                position: 'absolute',
                inset: '4px',
                zIndex: 4,
                display: 'flex',
                background: 'rgba(0,0,0,0.5)',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '6px',
              })}
              onClick={e => {
                e.stopPropagation()
                handleSelectStyle()
              }}
            >
              <TryAgainBtn />
            </div>
          ) : null}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              boxShadow: '0 0 0 4px white inset',
              zIndex: 2,
              borderRadius: '8px',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              boxShadow: '0 0 0 2px rgba(255, 5, 96, 1) inset',
              zIndex: 3,
              borderRadius: '8px',
            }}
          ></div>
        </>
      ) : null}

      <style>
        {`
          @keyframes clipCrop {
            0% {
              clip-path: inset(0 0 0 100%); /* hidden from left */
            }
            50% {
              clip-path: inset(0 0 0 0);    /* fully revealed */
            }
            100% {
              clip-path: inset(0 0 0 100%); /* hidden again from left */
            }
          }
          @keyframes swipeLine {
          0% {
            left: 100%;
          }
          50% {
            left: 0%;
          }
          100% {
            left: 100%;
          }
       }`}
      </style>
      {!hasPremium ? (
        <div style={{ position: 'absolute', top: '4px', right: '4px', zIndex: 4 }}>
          <Icons.PremiumIcon />
        </div>
      ) : null}
    </div>
  )
}

export default WrapAnimationImage
